import { useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import type { LupsEventStats } from './types';

interface Props {
  refreshKey: number;
  onFilterUnprocessed?: () => void;
}

const formatCLP = (n: number) => n.toLocaleString('es-CL');

interface MetricCardProps {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'neutral' | 'warn' | 'good' | 'bad';
  onClick?: () => void;
}

function MetricCard({ label, value, hint, tone = 'neutral', onClick }: MetricCardProps) {
  const toneClasses = {
    neutral: 'text-slate-900 dark:text-slate-100',
    warn: 'text-amber-700 dark:text-amber-400',
    good: 'text-emerald-700 dark:text-emerald-400',
    bad: 'text-rose-700 dark:text-rose-400',
  } as const;

  const Container = onClick ? 'button' : 'div';

  return (
    <Container
      onClick={onClick}
      className={`text-left rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 ${onClick ? 'cursor-pointer transition-all hover:border-slate-300 hover:shadow-md' : ''}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-2 font-mono text-3xl font-semibold tabular-nums ${toneClasses[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </Container>
  );
}

export default function LupsEventsStats({ refreshKey, onFilterUnprocessed }: Props) {
  const [stats, setStats] = useState<LupsEventStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const url = getAdminEndpointUrl('lupsEvents', 'stats');
        const data = await adminHttpClient.get<LupsEventStats>(url);
        if (!cancelled) setStats(data);
      } catch (err) {
        console.error('Error cargando stats Lups events', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-28 rounded-2xl border border-slate-200 bg-white animate-pulse dark:border-slate-800 dark:bg-slate-900" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Recibidos 24h" value={formatCLP(stats.total24h)} hint="Webhooks aceptados en las últimas 24h" />
        <MetricCard
          label="Sin procesar"
          value={formatCLP(stats.unprocessed)}
          hint={`${formatCLP(stats.unprocessedRecent)} en últimos 7d (el cron los retomará)`}
          tone={stats.unprocessedRecent > 0 ? 'warn' : 'neutral'}
          onClick={stats.unprocessed > 0 ? onFilterUnprocessed : undefined}
        />
        <MetricCard label="Pagos exitosos 24h" value={formatCLP(stats.succeeded24h)} tone="good" />
        <MetricCard label="Pagos fallidos 24h" value={formatCLP(stats.failed24h)} tone={stats.failed24h > 0 ? 'bad' : 'neutral'} />
      </div>

      {stats.byEventType.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Por tipo de evento (24h)</p>
          <div className="flex flex-wrap gap-2">
            {stats.byEventType.map((row) => (
              <span
                key={row.eventType}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-mono text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                <code>{row.eventType}</code>
                <span className="font-semibold tabular-nums">{formatCLP(row.count)}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
